import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
    Copy,
    Eye,
    EyeOff,
    Film,
    Image as ImageIcon,
    Magnet,
    Music,
    Palette,
    Scissors,
    Square,
    Trash2,
    Type,
    Volume2,
    VolumeX,
    ZoomIn,
    ZoomOut,
    Maximize,
    Diamond,
    Plus,
} from 'lucide-react';

import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { Slider } from '@/components/ui/slider';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import {
    AudioClip,
    AudioTrack,
    CompositionTrack,
    MainClip,
    OverlayLayer,
    OverlayTrack,
    VideoComposition,
    formatTime,
    itemEnd,
    mainInsertIndexAt,
    mainTrack,
    moveItem,
    reorderMainClip,
    snapPoints,
    snapSpan,
    snapTime,
    totalDuration,
    trimItem,
    updateItem,
} from './composition';
import { CompositionHistory } from './use-composition-history';
import { AudioWaveform } from './AudioWaveform';
import { useVideoPoster } from './media';

export type EditorSelection = { kind: 'clip' | 'layer' | 'audio' | 'transition'; id: string };

export interface DroppedMedia {
    kind: 'video' | 'image' | 'audio';
    src: string;
    assetUuid?: string;
    thumbnailUrl?: string;
    durationSec?: number;
    name?: string;
    aspect?: number;
}

export const MEDIA_DRAG_TYPE = 'application/x-ardealy-video-media';

const HEADER_W = 44;
const RULER_H = 26;
const ROW_H: Record<CompositionTrack['kind'], number> = { overlay: 30, main: 58, audio: 38 };
const SNAP_PX = 8;
export const MIN_PPS = 8;
export const MAX_PPS = 400;

interface TimelineProps {
    comp: VideoComposition;
    history: CompositionHistory;
    time: number;
    playing: boolean;
    onSeek: (t: number) => void;
    selection: EditorSelection | null;
    onSelect: (s: EditorSelection | null) => void;
    pxPerSec: number;
    onZoom: (pps: number) => void;
    snapping: boolean;
    onToggleSnapping: () => void;
    onSplit: () => void;
    onDelete: () => void;
    onDuplicate: () => void;
    onDropMedia: (media: DroppedMedia, at: { time: number; track: CompositionTrack }) => void;
}

interface DragBase {
    id: string;
    startX: number;
    startY: number;
    base: VideoComposition;
    moved: boolean;
    /** The state the gesture would commit. */
    result?: VideoComposition;
}

type Drag =
    | (DragBase & { mode: 'move'; trackKind: 'overlay' | 'audio'; origStart: number; duration: number })
    | (DragBase & { mode: 'reorder'; dx: number; pointerTime: number })
    | (DragBase & { mode: 'trim'; edge: 'start' | 'end'; edgeTime: number })
    | { mode: 'scrub' };

/** Seconds between labelled ticks for a zoom level. */
function tickStep(pps: number): number {
    const steps = [0.1, 0.25, 0.5, 1, 2, 5, 10, 15, 30, 60];
    return steps.find((s) => s * pps >= 64) ?? 60;
}

export const Timeline: React.FC<TimelineProps> = ({
    comp,
    history,
    time,
    playing,
    onSeek,
    selection,
    onSelect,
    pxPerSec: pps,
    onZoom,
    snapping,
    onToggleSnapping,
    onSplit,
    onDelete,
    onDuplicate,
    onDropMedia,
}) => {
    const { t } = useTranslation();
    const scrollRef = useRef<HTMLDivElement>(null);
    const dragRef = useRef<Drag | null>(null);
    const [drag, setDrag] = useState<Drag | null>(null);
    const [snapLine, setSnapLine] = useState<number | null>(null);
    const [dropHint, setDropHint] = useState<{ trackId: string; time: number } | null>(null);

    const duration = totalDuration(comp);
    const contentW = Math.max(600, (duration + 6) * pps);
    const mainClips = mainTrack(comp).clips;

    // Rows top to bottom: highest overlay first, then the picture, then sound.
    const rows = useMemo(() => {
        const overlays = comp.tracks.filter((tr) => tr.kind === 'overlay').reverse();
        const main = comp.tracks.filter((tr) => tr.kind === 'main');
        const audio = comp.tracks.filter((tr) => tr.kind === 'audio');
        return [...overlays, ...main, ...audio];
    }, [comp.tracks]);

    const timeAtClientX = useCallback((clientX: number) => {
        const el = scrollRef.current;
        if (!el) return 0;
        const rect = el.getBoundingClientRect();
        return Math.max(0, (clientX - rect.left + el.scrollLeft - HEADER_W) / pps);
    }, [pps]);

    // Keep the playhead in view while playing.
    useEffect(() => {
        const el = scrollRef.current;
        if (!el || !playing) return;
        const x = HEADER_W + time * pps;
        if (x > el.scrollLeft + el.clientWidth - 60) el.scrollLeft = x - HEADER_W - 40;
        else if (x < el.scrollLeft + HEADER_W) el.scrollLeft = Math.max(0, x - HEADER_W - 40);
    }, [time, playing, pps]);

    // Ctrl/⌘ + wheel zooms around the pointer.
    useEffect(() => {
        const el = scrollRef.current;
        if (!el) return undefined;
        const onWheel = (e: WheelEvent) => {
            if (!(e.ctrlKey || e.metaKey)) return;
            e.preventDefault();
            const before = timeAtClientX(e.clientX);
            const next = Math.max(MIN_PPS, Math.min(MAX_PPS, pps * (e.deltaY < 0 ? 1.15 : 1 / 1.15)));
            onZoom(next);
            requestAnimationFrame(() => {
                const rect = el.getBoundingClientRect();
                el.scrollLeft = Math.max(0, before * next + HEADER_W - (e.clientX - rect.left));
            });
        };
        el.addEventListener('wheel', onWheel, { passive: false });
        return () => el.removeEventListener('wheel', onWheel);
    }, [pps, onZoom, timeAtClientX]);

    // ─── pointer handling ───────────────────────────────────────────

    const threshold = SNAP_PX / pps;

    // Window listeners are stable wrappers around the latest handlers: the
    // component re-renders on every drag frame, and swapping listeners
    // mid-drag would drop the gesture.
    const handleMove = (e: PointerEvent) => {
        const d = dragRef.current;
        if (!d) return;
        if (d.mode === 'scrub') {
            onSeek(timeAtClientX(e.clientX));
            return;
        }
        const dxPx = e.clientX - d.startX;
        const dyPx = e.clientY - d.startY;
        if (!d.moved && Math.abs(dxPx) < 3 && Math.abs(dyPx) < 3) return;
        d.moved = true;
        const dt = dxPx / pps;
        if (d.mode === 'move') {
            let start = Math.max(0, d.origStart + dt);
            let line: number | null = null;
            if (snapping) {
                const s = snapSpan(start, d.duration, snapPoints(d.base, d.id, time), threshold);
                if (s.snapped) {
                    start = Math.max(0, s.start);
                    line = s.at ?? null;
                }
            }
            // The row under the pointer, if it holds this kind of item.
            let targetTrackId: string | undefined;
            for (const node of document.elementsFromPoint(e.clientX, e.clientY)) {
                const row = (node as HTMLElement).closest?.('[data-ve-track]') as HTMLElement | null;
                if (row && row.dataset.veKind === d.trackKind) {
                    targetTrackId = row.dataset.veTrack;
                    break;
                }
            }
            setSnapLine(line);
            d.result = moveItem(d.base, d.id, start, targetTrackId);
            history.preview(d.result);
        } else if (d.mode === 'trim') {
            let edge = d.edgeTime + dt;
            let line: number | null = null;
            if (snapping) {
                const s = snapTime(edge, snapPoints(d.base, d.id, time), threshold);
                if (s.snapped) {
                    edge = s.time;
                    line = s.time;
                }
            }
            setSnapLine(line);
            d.result = trimItem(d.base, d.id, d.edge, edge);
            history.preview(d.result);
        } else if (d.mode === 'reorder') {
            d.dx = dxPx;
            d.pointerTime = timeAtClientX(e.clientX);
            setDrag({ ...d });
        }
    };

    const handleUp = () => {
        const d = dragRef.current;
        dragRef.current = null;
        setDrag(null);
        setSnapLine(null);
        window.removeEventListener('pointermove', moveListener.current);
        window.removeEventListener('pointerup', upListener.current);
        if (!d || d.mode === 'scrub') return;
        if (!d.moved) {
            history.preview(d.base);
            return;
        }
        if (d.mode === 'reorder') {
            // Where it lands, judged against the reel as if the dragged clip were gone.
            let cursor = 0;
            const others = mainTrack(d.base).clips.filter((c) => c.id !== d.id).map((c) => {
                const laid = { ...c, start: cursor };
                cursor += c.duration;
                return laid;
            });
            history.commit(reorderMainClip(d.base, d.id, mainInsertIndexAt(others, d.pointerTime)));
            return;
        }
        if (d.result) history.commit(d.result);
    };

    const handlers = useRef({ move: handleMove, up: handleUp });
    handlers.current = { move: handleMove, up: handleUp };
    const moveListener = useRef((e: PointerEvent) => handlers.current.move(e));
    const upListener = useRef(() => handlers.current.up());

    const beginDrag = (d: Drag) => {
        dragRef.current = d;
        setDrag(d);
        window.addEventListener('pointermove', moveListener.current);
        window.addEventListener('pointerup', upListener.current);
    };

    useEffect(() => {
        const move = moveListener.current;
        const up = upListener.current;
        return () => {
            window.removeEventListener('pointermove', move);
            window.removeEventListener('pointerup', up);
        };
    }, []);

    const startScrub = (e: React.PointerEvent) => {
        e.preventDefault();
        onSeek(timeAtClientX(e.clientX));
        beginDrag({ mode: 'scrub' });
    };

    const startItem = (
        e: React.PointerEvent,
        track: CompositionTrack,
        item: MainClip | OverlayLayer | AudioClip,
        part: 'body' | 'start' | 'end',
    ) => {
        if (e.button !== 0) return;
        e.stopPropagation();
        e.preventDefault();
        const kind: EditorSelection['kind'] = track.kind === 'main' ? 'clip' : track.kind === 'audio' ? 'audio' : 'layer';
        onSelect({ kind, id: item.id });
        const base = history.committed();
        if (part !== 'body') {
            beginDrag({
                mode: 'trim',
                id: item.id,
                edge: part,
                startX: e.clientX,
                startY: e.clientY,
                base,
                edgeTime: part === 'start' ? item.start : itemEnd(item),
                moved: false,
            });
            return;
        }
        if (track.kind === 'main') {
            beginDrag({ mode: 'reorder', id: item.id, startX: e.clientX, startY: e.clientY, base, dx: 0, moved: false, pointerTime: timeAtClientX(e.clientX) });
            return;
        }
        beginDrag({
            mode: 'move',
            id: item.id,
            trackKind: track.kind,
            startX: e.clientX,
            startY: e.clientY,
            base,
            origStart: item.start,
            duration: item.duration,
            moved: false,
        });
    };

    // ─── drop from the media bin ────────────────────────────────────

    const readDrop = (e: React.DragEvent): DroppedMedia | null => {
        try {
            const raw = e.dataTransfer.getData(MEDIA_DRAG_TYPE);
            return raw ? JSON.parse(raw) : null;
        } catch {
            return null;
        }
    };

    const rowAccepts = (track: CompositionTrack, types: readonly string[]) =>
        types.includes(MEDIA_DRAG_TYPE) && !!track;

    // ─── render ─────────────────────────────────────────────────────

    const step = tickStep(pps);
    const minor = step / (step >= 1 ? 4 : 2);
    const ticks: number[] = [];
    for (let s = 0; s <= contentW / pps; s += step) ticks.push(Math.round(s * 1000) / 1000);

    const reorderInsertX = (() => {
        if (!drag || drag.mode !== 'reorder' || !drag.moved) return null;
        const others = mainClips.filter((c) => c.id !== drag.id);
        let cursor = 0;
        const laid = others.map((c) => {
            const out = { ...c, start: cursor };
            cursor += c.duration;
            return out;
        });
        const idx = mainInsertIndexAt(laid, drag.pointerTime);
        return (idx < laid.length ? laid[idx].start : cursor) * pps;
    })();

    const isSelected = (id: string) => selection?.id === id;

    return (
        <div className="flex h-full min-h-0 flex-col border-t bg-background">
            {/* Toolbar */}
            <div className="flex h-10 shrink-0 items-center gap-1 border-b px-2">
                <ToolButton label={t('VIDEO_EDITOR.TIMELINE.SPLIT')} shortcut="S" onClick={onSplit} disabled={!selection || selection.kind === 'transition'}>
                    <Scissors />
                </ToolButton>
                <ToolButton label={t('VIDEO_EDITOR.TIMELINE.DUPLICATE')} shortcut="⌘D" onClick={onDuplicate} disabled={!selection || selection.kind === 'transition'}>
                    <Copy />
                </ToolButton>
                <ToolButton label={t('VIDEO_EDITOR.TIMELINE.DELETE')} shortcut="⌫" onClick={onDelete} disabled={!selection}>
                    <Trash2 />
                </ToolButton>
                <div className="mx-1 h-5 w-px bg-border" />
                <ToolButton label={t('VIDEO_EDITOR.TIMELINE.SNAPPING')} onClick={onToggleSnapping} active={snapping}>
                    <Magnet />
                </ToolButton>
                <div className="ms-auto flex items-center gap-2">
                    <span className="text-xs tabular-nums text-muted-foreground">
                        {formatTime(time, comp.fps)} / {formatTime(duration, comp.fps)}
                    </span>
                    <div className="mx-1 h-5 w-px bg-border" />
                    <ToolButton label={t('VIDEO_EDITOR.TIMELINE.ZOOM_OUT')} onClick={() => onZoom(Math.max(MIN_PPS, pps / 1.4))}>
                        <ZoomOut />
                    </ToolButton>
                    <Slider
                        className="w-28"
                        value={[Math.log(pps)]}
                        min={Math.log(MIN_PPS)}
                        max={Math.log(MAX_PPS)}
                        step={0.01}
                        onValueChange={(v: number[]) => onZoom(Math.exp(v[0]))}
                    />
                    <ToolButton label={t('VIDEO_EDITOR.TIMELINE.ZOOM_IN')} onClick={() => onZoom(Math.min(MAX_PPS, pps * 1.4))}>
                        <ZoomIn />
                    </ToolButton>
                    <ToolButton
                        label={t('VIDEO_EDITOR.TIMELINE.FIT')}
                        onClick={() => {
                            const w = (scrollRef.current?.clientWidth ?? 800) - HEADER_W - 48;
                            onZoom(Math.max(MIN_PPS, Math.min(MAX_PPS, w / Math.max(1, duration))));
                            if (scrollRef.current) scrollRef.current.scrollLeft = 0;
                        }}
                    >
                        <Maximize />
                    </ToolButton>
                </div>
            </div>

            {/* Tracks */}
            <div ref={scrollRef} className="relative min-h-0 flex-1 overflow-auto select-none" data-ve-timeline>
                <div className="relative" style={{ width: contentW + HEADER_W, minHeight: '100%' }}>
                    {/* Ruler */}
                    <div className="sticky top-0 z-30 flex" style={{ height: RULER_H }}>
                        <div className="sticky start-0 z-10 shrink-0 border-e border-b bg-background" style={{ width: HEADER_W }} />
                        <div
                            className="relative flex-1 cursor-pointer border-b bg-background"
                            onPointerDown={startScrub}
                            style={{
                                backgroundImage: 'linear-gradient(to right, var(--border) 1px, transparent 1px)',
                                backgroundSize: `${minor * pps}px 6px`,
                                backgroundRepeat: 'repeat-x',
                                backgroundPosition: 'left bottom',
                            }}
                        >
                            {ticks.map((s) => (
                                <div key={s} className="absolute bottom-0 flex h-full items-end" style={{ left: s * pps }}>
                                    <div className="h-2.5 w-px bg-muted-foreground/50" />
                                    <span className="mb-2 ms-1 text-[10px] tabular-nums text-muted-foreground">{formatTime(s)}</span>
                                </div>
                            ))}
                        </div>
                    </div>

                    {/* Rows */}
                    {rows.map((track) => (
                        <div
                            key={track.id}
                            className="relative flex border-b border-border/60"
                            style={{ height: ROW_H[track.kind] + 8 }}
                        >
                            <TrackHeader
                                track={track}
                                onToggle={() => {
                                    if (track.kind === 'overlay') {
                                        history.commit((c) => ({
                                            ...c,
                                            tracks: c.tracks.map((x) => (x.id === track.id ? { ...(x as OverlayTrack), hidden: !(x as OverlayTrack).hidden } : x)),
                                        }));
                                    } else if (track.kind === 'audio') {
                                        history.commit((c) => ({
                                            ...c,
                                            tracks: c.tracks.map((x) => (x.id === track.id ? { ...(x as AudioTrack), muted: !(x as AudioTrack).muted } : x)),
                                        }));
                                    }
                                }}
                            />
                            <div
                                className={cn(
                                    'relative flex-1',
                                    track.kind === 'main' && 'bg-muted/40',
                                    dropHint?.trackId === track.id && 'bg-muted',
                                )}
                                data-ve-track={track.id}
                                data-ve-kind={track.kind}
                                onPointerDown={(e) => {
                                    if (e.button !== 0) return;
                                    onSelect(null);
                                    startScrub(e);
                                }}
                                onDragOver={(e) => {
                                    if (!rowAccepts(track, e.dataTransfer.types)) return;
                                    e.preventDefault();
                                    e.dataTransfer.dropEffect = 'copy';
                                    setDropHint({ trackId: track.id, time: timeAtClientX(e.clientX) });
                                }}
                                onDragLeave={() => setDropHint(null)}
                                onDrop={(e) => {
                                    const m = readDrop(e);
                                    setDropHint(null);
                                    if (!m) return;
                                    e.preventDefault();
                                    onDropMedia(m, { time: timeAtClientX(e.clientX), track });
                                }}
                            >
                                {track.kind === 'main' && track.clips.map((clip, i) => {
                                    const draggingThis = drag?.mode === 'reorder' && drag.id === clip.id && drag.moved;
                                    return (
                                        <React.Fragment key={clip.id}>
                                            <ItemBox
                                                left={clip.start * pps}
                                                width={clip.duration * pps}
                                                height={ROW_H.main}
                                                selected={isSelected(clip.id)}
                                                className={cn('bg-stone-800', draggingThis && 'z-20 opacity-80 shadow-lg')}
                                                style={{
                                                    transform: draggingThis ? `translateX(${(drag as any).dx}px)` : undefined,
                                                }}
                                                onPointerDown={(e, part) => startItem(e, track, clip, part)}
                                            >
                                                <ClipFilmstrip clip={clip} />
                                                <span className="absolute bottom-1 start-1.5 flex items-center gap-1 rounded bg-black/55 px-1 text-[10px] tabular-nums text-white">
                                                    {clip.kind === 'video' ? <Film className="size-2.5" /> : clip.kind === 'color' ? <Palette className="size-2.5" /> : <ImageIcon className="size-2.5" />}
                                                    {clip.duration.toFixed(1)}s
                                                    {clip.kind === 'video' && clip.speed && clip.speed !== 1 ? <span className="opacity-75">{`· ${clip.speed}×`}</span> : null}
                                                </span>
                                            </ItemBox>
                                            {i > 0 && (
                                                <TransitionMarker
                                                    clip={clip}
                                                    pps={pps}
                                                    selected={selection?.kind === 'transition' && selection.id === clip.id}
                                                    onClick={() => {
                                                        if (!clip.transitionIn) {
                                                            history.commit(updateItem<MainClip>(comp, clip.id, { transitionIn: { type: 'crossfade', duration: 0.6 } }));
                                                        }
                                                        onSelect({ kind: 'transition', id: clip.id });
                                                    }}
                                                    label={t('VIDEO_EDITOR.TIMELINE.TRANSITION')}
                                                />
                                            )}
                                        </React.Fragment>
                                    );
                                })}
                                {track.kind === 'main' && reorderInsertX !== null && (
                                    <div className="pointer-events-none absolute top-0 z-30 h-full w-0.5 bg-foreground" style={{ left: reorderInsertX - 1 }} />
                                )}
                                {track.kind === 'main' && track.clips.length === 0 && (
                                    <div className="pointer-events-none absolute inset-y-1 start-1 flex items-center rounded-lg border border-dashed px-3 text-xs text-muted-foreground" style={{ width: Math.max(240, 6 * pps) }}>
                                        {t('VIDEO_EDITOR.TIMELINE.EMPTY_MAIN')}
                                    </div>
                                )}
                                {track.kind === 'overlay' && track.layers.map((layer) => (
                                    <ItemBox
                                        key={layer.id}
                                        left={layer.start * pps}
                                        width={layer.duration * pps}
                                        height={ROW_H.overlay}
                                        selected={isSelected(layer.id)}
                                        className={cn(
                                            'border-border bg-secondary text-foreground',
                                            track.hidden && 'opacity-40',
                                        )}
                                        onPointerDown={(e, part) => startItem(e, track, layer, part)}
                                    >
                                        <span className="flex h-full items-center gap-1.5 overflow-hidden px-2 text-[11px]">
                                            {layer.kind === 'text' ? <Type className="size-3 shrink-0" /> : layer.kind === 'image' ? <ImageIcon className="size-3 shrink-0" /> : <Square className="size-3 shrink-0" />}
                                            <span className="truncate">
                                                {layer.kind === 'text'
                                                    ? layer.text.replace(/\n/g, ' ')
                                                    : layer.kind === 'image'
                                                    ? t('VIDEO_EDITOR.LAYER.IMAGE')
                                                    : t(`VIDEO_EDITOR.SHAPE.${layer.shape.toUpperCase()}`)}
                                            </span>
                                        </span>
                                    </ItemBox>
                                ))}
                                {track.kind === 'audio' && track.clips.map((clip) => (
                                    <ItemBox
                                        key={clip.id}
                                        left={clip.start * pps}
                                        width={clip.duration * pps}
                                        height={ROW_H.audio}
                                        selected={isSelected(clip.id)}
                                        className={cn('border-border bg-muted text-foreground', track.muted && 'opacity-40')}
                                        onPointerDown={(e, part) => startItem(e, track, clip, part)}
                                    >
                                        <AudioWaveform clip={clip} width={Math.max(4, clip.duration * pps)} height={ROW_H.audio} />
                                        <span className="absolute start-1.5 top-0.5 flex max-w-[90%] items-center gap-1 truncate text-[10px] text-muted-foreground">
                                            <Music className="size-2.5 shrink-0" />
                                            <span className="truncate">{clip.name || t('VIDEO_EDITOR.LAYER.AUDIO')}</span>
                                        </span>
                                    </ItemBox>
                                ))}
                                {dropHint?.trackId === track.id && (
                                    <div className="pointer-events-none absolute top-0 z-30 h-full w-0.5 bg-foreground/60" style={{ left: dropHint.time * pps }} />
                                )}
                            </div>
                        </div>
                    ))}

                    {/* Snap guide */}
                    {snapLine !== null && (
                        <div className="pointer-events-none absolute bottom-0 z-30 w-px bg-amber-500" style={{ left: HEADER_W + snapLine * pps, top: RULER_H }} />
                    )}

                    {/* Playhead */}
                    <div
                        className="pointer-events-none absolute bottom-0 top-0 z-40"
                        style={{ left: HEADER_W + time * pps }}
                    >
                        <div className="absolute -start-[5px] top-[14px] size-[11px] rotate-45 rounded-[2px] bg-foreground" />
                        <div className="absolute start-0 top-[18px] bottom-0 w-px bg-foreground" />
                    </div>
                </div>
            </div>
        </div>
    );
};

const ToolButton: React.FC<{
    label: string;
    shortcut?: string;
    onClick: () => void;
    disabled?: boolean;
    active?: boolean;
    children: React.ReactNode;
}> = ({ label, shortcut, onClick, disabled, active, children }) => (
    <Tooltip>
        <TooltipTrigger asChild>
            <Button
                type="button"
                variant="ghost"
                size="icon-sm"
                aria-label={label}
                aria-pressed={active}
                disabled={disabled}
                onClick={onClick}
                className={cn('text-muted-foreground hover:text-foreground', active && 'bg-muted text-foreground')}
            >
                {children}
            </Button>
        </TooltipTrigger>
        <TooltipContent side="top">
            {label}
            {shortcut && <span className="ms-2 text-muted-foreground">{shortcut}</span>}
        </TooltipContent>
    </Tooltip>
);

const TrackHeader: React.FC<{ track: CompositionTrack; onToggle: () => void }> = ({ track, onToggle }) => {
    const icon = track.kind === 'main' ? <Film /> : track.kind === 'overlay' ? <Type /> : <Music />;
    const toggle = track.kind === 'overlay'
        ? (track.hidden ? <EyeOff /> : <Eye />)
        : track.kind === 'audio'
        ? (track.muted ? <VolumeX /> : <Volume2 />)
        : null;
    return (
        <div
            className="sticky start-0 z-20 flex shrink-0 flex-col items-center justify-center gap-1 border-e bg-background text-muted-foreground [&_svg]:size-3.5"
            style={{ width: HEADER_W }}
        >
            {icon}
            {toggle && (
                <button type="button" onClick={onToggle} className="rounded p-0.5 hover:bg-muted hover:text-foreground">
                    {toggle}
                </button>
            )}
        </div>
    );
};

const ItemBox: React.FC<{
    left: number;
    width: number;
    height: number;
    selected: boolean;
    className?: string;
    style?: React.CSSProperties;
    onPointerDown: (e: React.PointerEvent, part: 'body' | 'start' | 'end') => void;
    children?: React.ReactNode;
}> = ({ left, width, height, selected, className, style, onPointerDown, children }) => (
    <div
        className={cn(
            'group/item absolute top-1 cursor-grab overflow-hidden rounded-lg border active:cursor-grabbing',
            selected ? 'z-10 border-foreground outline outline-[1.5px] outline-foreground' : 'border-black/10',
            className,
        )}
        style={{ left, width: Math.max(4, width - 1), height, ...style }}
        onPointerDown={(e) => onPointerDown(e, 'body')}
    >
        {children}
        <div
            className={cn(
                'absolute inset-y-0 start-0 z-10 w-2 cursor-ew-resize',
                selected ? 'bg-foreground/80' : 'opacity-0 group-hover/item:opacity-100 group-hover/item:bg-foreground/30',
            )}
            onPointerDown={(e) => onPointerDown(e, 'start')}
        />
        <div
            className={cn(
                'absolute inset-y-0 end-0 z-10 w-2 cursor-ew-resize',
                selected ? 'bg-foreground/80' : 'opacity-0 group-hover/item:opacity-100 group-hover/item:bg-foreground/30',
            )}
            onPointerDown={(e) => onPointerDown(e, 'end')}
        />
    </div>
);

const TransitionMarker: React.FC<{
    clip: MainClip;
    pps: number;
    selected: boolean;
    onClick: () => void;
    label: string;
}> = ({ clip, pps, selected, onClick, label }) => {
    const has = !!clip.transitionIn;
    const center = (clip.start + (clip.transitionIn?.duration ?? 0) / 2) * pps;
    return (
        <>
            {has && (
                <div
                    className="pointer-events-none absolute top-1 z-[11] rounded-md bg-white/25"
                    style={{ left: clip.start * pps, width: (clip.transitionIn!.duration) * pps, height: ROW_H.main }}
                />
            )}
            <button
                type="button"
                title={label}
                aria-label={label}
                onPointerDown={(e) => e.stopPropagation()}
                onClick={(e) => { e.stopPropagation(); onClick(); }}
                className={cn(
                    'absolute top-1/2 z-[12] flex size-5 -translate-x-1/2 -translate-y-1/2 items-center justify-center rounded-md border bg-background text-foreground transition-opacity [&_svg]:size-3',
                    has || selected ? 'opacity-100' : 'opacity-0 hover:opacity-100 focus:opacity-100',
                    selected ? 'border-foreground outline outline-[1.5px] outline-foreground' : 'border-border',
                )}
                style={{ left: center }}
            >
                {has ? <Diamond /> : <Plus />}
            </button>
        </>
    );
};

/** A clip's picture repeated along its length, like a film strip. */
const ClipFilmstrip: React.FC<{ clip: MainClip }> = ({ clip }) => {
    const poster = useVideoPoster(clip.src, clip.kind === 'image' ? clip.src : clip.thumbnailUrl, clip.kind === 'video');
    if (clip.kind === 'color') {
        // A colour card is its own swatch.
        return <span className="pointer-events-none absolute inset-0" style={{ background: clip.color || '#000000' }} />;
    }
    if (!poster) {
        return <span className="absolute inset-0 flex items-center justify-center text-white/60"><Film className="size-4" /></span>;
    }
    return (
        <span
            className="pointer-events-none absolute inset-0"
            style={{
                backgroundImage: `url(${poster})`,
                backgroundSize: `${Math.round(ROW_H.main * 0.75)}px 100%`,
                backgroundRepeat: 'repeat-x',
            }}
        />
    );
};
